import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAchievements, type TrackedAchievement } from "@/hooks/useAchievements";
import { useMissions, type Mission } from "@/hooks/useMissions";
import { usePlayerProgression } from "@/hooks/usePlayerProgression";
import { useStreaks } from "@/hooks/useStreaks";
import { notifyWalletUpdated, useWallet } from "@/hooks/useWallet";
import { ArrowLeft, Trophy, Lock, Sparkles, Target, CheckCircle2, Coins, Flame, BookOpen, Shield, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import HoloPlacard from "@/components/holo/HoloPlacard";
import HoloProgressBar from "@/components/holo/HoloProgressBar";
import HoloStatBadge from "@/components/holo/HoloStatBadge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const RARITY_META: Record<string, { label: string; badgeVariant: "primary" | "secondary" | "accent" | "muted" }> = {
  common: { label: "Common", badgeVariant: "muted" },
  uncommon: { label: "Uncommon", badgeVariant: "primary" },
  rare: { label: "Rare", badgeVariant: "secondary" },
  epic: { label: "Epic", badgeVariant: "accent" },
  legendary: { label: "Legendary", badgeVariant: "accent" },
};

const STATUS_META: Record<TrackedAchievement["status"], { label: string; tone: string; badgeVariant: "primary" | "secondary" | "accent" | "muted" }> = {
  locked: { label: "Locked", tone: "text-muted-foreground", badgeVariant: "muted" },
  in_progress: { label: "In Progress", tone: "text-primary", badgeVariant: "primary" },
  completed: { label: "Completed", tone: "text-secondary", badgeVariant: "secondary" },
  claimed: { label: "Claimed", tone: "text-accent", badgeVariant: "accent" },
};

const CATEGORY_META: Record<string, { label: string; color: string }> = {
  learning: { label: "Learning", color: "text-primary" },
  financial: { label: "Financial", color: "text-accent" },
  simulator: { label: "Simulators", color: "text-secondary" },
  milestone: { label: "Milestones", color: "text-primary" },
  general: { label: "General", color: "text-muted-foreground" },
};

function MissionRow({ mission, onClaim, claiming }: { mission: Mission; onClaim: (missionId: string) => Promise<void>; claiming: boolean }) {
  const percent = mission.target_count > 0 ? Math.min(100, Math.round((mission.current_count / mission.target_count) * 100)) : 0;
  const isClaimable = mission.status === "completed";
  const isClaimed = mission.status === "claimed";

  return (
    <div className="rounded-2xl border border-border/50 bg-card/50 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading font-semibold text-sm text-foreground">{mission.title}</p>
            <HoloStatBadge label="" value={mission.mission_type === "daily" ? "Daily" : "Weekly"} variant={mission.mission_type === "daily" ? "primary" : "secondary"} className="py-0 px-1.5 text-[9px]" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">{mission.description}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs font-mono text-foreground">{mission.current_count}/{mission.target_count}</p>
        </div>
      </div>
      <HoloProgressBar value={percent} size="sm" variant={isClaimed ? "accent" : isClaimable ? "secondary" : "primary"} />
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {mission.xp_reward > 0 && <HoloStatBadge label="XP" value={mission.xp_reward} variant="primary" />}
          {mission.coin_reward > 0 && <HoloStatBadge icon={<Coins className="w-3 h-3" />} label="" value={`$${mission.coin_reward}`} variant="accent" />}
        </div>
        {isClaimable ? (
          <Button size="sm" onClick={() => void onClaim(mission.id)} disabled={claiming}>Claim</Button>
        ) : (
          <span className="text-[11px] text-muted-foreground">{isClaimed ? "Claimed" : "Keep going"}</span>
        )}
      </div>
    </div>
  );
}

function AchievementCard({ achievement, onClaim, claiming }: { achievement: TrackedAchievement; onClaim?: (achievementId: string) => Promise<void>; claiming: boolean }) {
  const rarity = RARITY_META[achievement.rarity] || RARITY_META.common;
  const status = STATUS_META[achievement.status];
  const rewardAmount = achievement.reward_amount ?? 0;
  const hasValidReward = rewardAmount > 0;
  const showClaim = achievement.status === "completed" && onClaim && hasValidReward;

  return (
    <HoloPlacard variant={achievement.is_unlocked || achievement.is_claimable ? "glow" : "default"} className={cn("p-4", achievement.status === "locked" && "opacity-70")}>
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className={cn("w-10 h-10 rounded-xl border flex items-center justify-center text-base shrink-0", achievement.status === "locked" ? "bg-muted/20 border-border/40" : "bg-primary/10 border-primary/20")}>
            {achievement.status === "locked" ? <Lock className="w-4 h-4 text-muted-foreground" /> : (achievement.icon || "🏆")}
          </div>
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-heading font-semibold text-sm text-foreground truncate">{achievement.name}</p>
              <HoloStatBadge label="" value={rarity.label} variant={rarity.badgeVariant} className="py-0 px-1.5 text-[8px]" />
            </div>
            <p className="text-[11px] text-muted-foreground">{achievement.description}</p>
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-mono text-foreground">{achievement.progress_current}/{achievement.progress_target}</span>
          </div>
          <HoloProgressBar value={achievement.completion_pct} size="sm" variant={achievement.is_claimable ? "secondary" : achievement.is_unlocked ? "accent" : "primary"} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-muted-foreground">{hasValidReward ? `$${rewardAmount} reward` : ""}</span>
          {showClaim ? (
            <Button size="sm" onClick={() => void onClaim?.(achievement.id)} disabled={claiming}>{claiming ? "..." : "Claim"}</Button>
          ) : achievement.status === "claimed" ? (
            <span className="text-[11px] text-accent font-medium">Claimed</span>
          ) : (
            <span className={cn("text-[11px] font-medium", status.tone)}>{achievement.completion_pct}%</span>
          )}
        </div>
      </div>
    </HoloPlacard>
  );
}

const RewardsScreen = () => {
  const navigate = useNavigate();
  const { refetch: refetchWallet } = useWallet();
  const { playerLevel, titleLadder } = usePlayerProgression();
  const { streaks } = useStreaks();
  const {
    achievements, totalUnlocked, totalCount, completionPct,
    claimableAchievements, inProgressAchievements, completedAchievements, lockedAchievements,
    loading, claimReward,
  } = useAchievements();
  const { dailyMissions, weeklyMissions, loading: missionsLoading, claimReward: claimMissionReward } = useMissions();
  const [claimingAchievementId, setClaimingAchievementId] = useState<string | null>(null);
  const [claimingMissionId, setClaimingMissionId] = useState<string | null>(null);

  const allMissions = useMemo(() => [...dailyMissions, ...weeklyMissions], [dailyMissions, weeklyMissions]);

  const xpPct = playerLevel.xp_for_next_level > 0
    ? Math.round((playerLevel.xp_into_current_level / playerLevel.xp_for_next_level) * 100)
    : 0;

  const handleAchievementClaim = async (achievementId: string) => {
    if (claimingAchievementId) return;
    setClaimingAchievementId(achievementId);
    try {
      const result = await claimReward(achievementId);
      if (!result.ok) { toast.error(result.error || "Unable to claim."); return; }
      if (result.already_claimed) { toast.info(`${result.achievement_name} already claimed.`); return; }
      await refetchWallet();
      notifyWalletUpdated();
      toast.success(`${result.achievement_name} claimed · $${result.reward_amount} added`);
    } catch { toast.error("Unable to claim reward."); } finally { setClaimingAchievementId(null); }
  };

  const handleMissionClaim = async (missionId: string) => {
    if (claimingMissionId) return;
    setClaimingMissionId(missionId);
    const result = await claimMissionReward(missionId);
    if (result) toast.success(`Mission reward claimed${result.coins > 0 ? ` · +$${result.coins}` : ""}${result.xp > 0 ? ` · +${result.xp} XP` : ""}`);
    setClaimingMissionId(null);
  };

  if (loading || missionsLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const recentBadges = achievements.filter((a) => a.is_unlocked).slice(0, 8);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container max-w-4xl mx-auto flex items-center gap-3 py-3 px-3 sm:py-4 sm:px-4">
          <Button variant="ghost" size="icon" aria-label="Go back" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-heading font-bold text-xl text-foreground">Rewards & Progression</h1>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto py-6 px-3 sm:px-4 space-y-6">
        {/* ──── Level & XP ──── */}
        <HoloPlacard variant="glow" className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading font-bold text-2xl text-gradient-blue">Level {playerLevel.current_level}</p>
                <p className="text-xs text-primary font-medium">{playerLevel.current_title}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="font-heading font-bold text-lg text-foreground">{playerLevel.total_xp.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">Total XP</p>
            </div>
          </div>

          <div className="space-y-1">
            <Progress value={xpPct} className="h-2.5" />
            <div className="flex justify-between text-[11px] text-muted-foreground">
              <span>{playerLevel.xp_into_current_level} / {playerLevel.xp_for_next_level} XP to next level</span>
              <span>{xpPct}%</span>
            </div>
          </div>

          {/* Title ladder */}
          <div className="pt-3 border-t border-border/50">
            <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">Title Progression</p>
            <div className="flex flex-wrap gap-1.5">
              {titleLadder.map((t) => {
                const reached = playerLevel.current_level >= t.level;
                const isCurrent = playerLevel.current_title === t.title;
                return (
                  <span key={t.level} className={cn(
                    "text-[10px] px-2 py-0.5 rounded-md border font-medium",
                    isCurrent ? "bg-primary/15 border-primary/40 text-primary"
                      : reached ? "bg-muted/50 border-border text-foreground"
                        : "bg-muted/20 border-border/50 text-muted-foreground/50"
                  )}>{t.title}</span>
                );
              })}
            </div>
          </div>
        </HoloPlacard>

        {/* ──── Streaks ──── */}
        <HoloPlacard className="p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Streaks</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-primary" />
                <span className="font-heading font-bold text-2xl text-foreground">{streaks.login_streak}</span>
              </div>
              <p className="text-xs text-muted-foreground">Login Streak</p>
              <p className="text-[10px] text-muted-foreground/70">Best: {streaks.longest_login_streak}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" />
                <span className="font-heading font-bold text-2xl text-foreground">{streaks.learning_streak}</span>
              </div>
              <p className="text-xs text-muted-foreground">Learning Streak</p>
              <p className="text-[10px] text-muted-foreground/70">Best: {streaks.longest_learning_streak}</p>
            </div>
          </div>
          {streaks.freeze_tokens > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-accent" />
              <span className="text-xs text-muted-foreground">{streaks.freeze_tokens} Freeze Token{streaks.freeze_tokens > 1 ? "s" : ""}</span>
            </div>
          )}
        </HoloPlacard>

        {/* ──── Recent Badges ──── */}
        {recentBadges.length > 0 && (
          <HoloPlacard className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-secondary" />
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Recent Badges</span>
              </div>
              <span className="text-xs text-muted-foreground">{totalUnlocked}/{totalCount}</span>
            </div>
            <HoloProgressBar value={completionPct} size="sm" variant="secondary" className="mb-4" />
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {recentBadges.map((a) => (
                <div key={a.id} className="aspect-square rounded-xl border border-primary/20 bg-primary/5 flex flex-col items-center justify-center gap-0.5" title={a.name}>
                  <span className="text-base">{a.icon || "🏆"}</span>
                  <span className="text-[7px] text-center font-medium text-foreground/80 px-0.5 leading-tight truncate w-full">{a.name}</span>
                </div>
              ))}
            </div>
          </HoloPlacard>
        )}

        {/* ──── Missions ──── */}
        {allMissions.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <h2 className="font-heading font-bold text-lg text-foreground">Missions</h2>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {allMissions.map((mission) => (
                <MissionRow key={mission.id} mission={mission} onClaim={handleMissionClaim} claiming={claimingMissionId === mission.id} />
              ))}
            </div>
          </section>
        )}

        {/* ──── Claimable ──── */}
        {claimableAchievements.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-secondary" />
              <h2 className="font-heading font-bold text-lg text-foreground">Ready to Claim</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {claimableAchievements.map((a) => (
                <AchievementCard key={a.id} achievement={a} onClaim={handleAchievementClaim} claiming={claimingAchievementId === a.id} />
              ))}
            </div>
          </section>
        )}

        {/* ──── In Progress ──── */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h2 className="font-heading font-bold text-lg text-foreground">In Progress</h2>
          </div>
          {inProgressAchievements.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {inProgressAchievements.map((a) => <AchievementCard key={a.id} achievement={a} claiming={claimingAchievementId === a.id} />)}
            </div>
          ) : (
            <HoloPlacard className="p-5 text-center text-sm text-muted-foreground">No active progress yet.</HoloPlacard>
          )}
        </section>

        {/* ──── Completed & Claimed ──── */}
        {completedAchievements.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-accent" />
              <h2 className="font-heading font-bold text-lg text-foreground">Completed</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {completedAchievements.map((a) => <AchievementCard key={a.id} achievement={a} claiming={claimingAchievementId === a.id} />)}
            </div>
          </section>
        )}

        {/* ──── Locked ──── */}
        {lockedAchievements.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <h2 className="font-heading font-bold text-lg text-foreground">Locked</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {lockedAchievements.map((a) => <AchievementCard key={a.id} achievement={a} claiming={claimingAchievementId === a.id} />)}
            </div>
          </section>
        )}

        {/* ──── Categories ──── */}
        <section className="space-y-3">
          <h2 className="font-heading font-bold text-lg text-foreground">Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(CATEGORY_META).map(([key, meta]) => {
              const catAchievements = achievements.filter((a) => a.category === key);
              const catUnlocked = catAchievements.filter((a) => a.is_unlocked).length;
              return (
                <div key={key} className="rounded-2xl border border-border/50 bg-card/50 p-3 text-center">
                  <p className={cn("text-[10px] uppercase tracking-wider", meta.color)}>{meta.label}</p>
                  <p className="font-heading font-bold text-lg text-foreground">{catUnlocked}/{catAchievements.length}</p>
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
};

export default RewardsScreen;
